import { AnyAction, Dispatch } from 'redux';
import { ReduxAxiosAction } from 'redux-axios-middleware';

import Logger, { LoggingModule } from 'services/Logger';
import { GetState, RootDispatch, RootState, Thunk } from 'store/types';
import { clearProviderForBooking } from 'store/cost/actions';
import { ProviderSummary } from 'store/findProvider/types';
import { profileConsumerSelector } from 'store/profile/selectors';
import { allProvidersSelector, patientsDataSelector } from 'store/booking/selectors';
import {
  CLEAR_GROUPED_VISIT_TYPE,
  clearCorrelationIDAction,
  getPatients,
  validateSelectedPatientInsurance
} from 'store/booking/actions';
import { getLocationTimeSlots } from 'store/booking/appointment/timeSlots/actions';
import { currentAccountConsumerIdSelector } from 'store/account/selectors';
import {
  CancelAppointmentRequestPayload,
  RescheduleAppointmentRequestPayload,
  KEY,
  LockSlotRequestPayload,
  Patient,
  Specialty,
  SubmitAppointmentRequestPayload,
  TimeSlot,
  BookedAppointment,
  Slots
} from 'store/booking/types';

export const SET_PATIENT = `${KEY}.SET_PATIENT`;

export const setPatient = (patient: Patient) => ({
  type: SET_PATIENT,
  payload: patient
});

export const SET_SPECIALTY = `${KEY}.SET_SPECIALTY`;

export const setSpecialty = (specialty: Specialty) => ({
  type: SET_SPECIALTY,
  payload: specialty
});

export const SET_DOCTOR = `${KEY}.SET_DOCTOR`;

export const setDoctor = (doctor: ProviderSummary) => ({
  type: SET_DOCTOR,
  payload: doctor
});

export const SET_APPOINTMENT_DATE = `${KEY}.SET_APPOINTMENT_DATE`;

export const setAppointmentDate = (appointmentDate: Date | string) => ({
  type: SET_APPOINTMENT_DATE,
  payload: appointmentDate
});

export const SET_APPOINTMENT_TIME = `${KEY}.SET_APPOINTMENT_TIME`;

export const setAppointmentTime = (appointmentTime: TimeSlot | Slots) => ({
  type: SET_APPOINTMENT_TIME,
  payload: appointmentTime
});

export const RESET_APPOINTMENT_TIME = `${KEY}.RESET_APPOINTMENT_TIME`;

export const resetAppointmentTime = () => ({
  type: RESET_APPOINTMENT_TIME
});

export const SET_FETCH_SLOTS = `${KEY}.SET_FETCH_SLOTS`;

export const setFetchSlots = (fetchSlots: boolean) => ({
  type: SET_FETCH_SLOTS,
  payload: fetchSlots
});

export const SET_UPDATED_VISIT_GROUP = 'SET_UPDATED_VISIT_GROUP';

export const setUpdatedVisitGroup = (updatedVisitGroup: boolean) => ({
  type: SET_UPDATED_VISIT_GROUP,
  payload: updatedVisitGroup
});

export const SET_IS_EXTERNAL_REQUEST = `${KEY}.SET_IS_EXTERNAL_REQUEST`;

export const setIsExternalRequest = (isExternalRequest: boolean) => ({
  type: SET_IS_EXTERNAL_REQUEST,
  payload: isExternalRequest
});

/**
 * Lock Appointment Slot
 */
export const LOCK_TIME_SLOT = `${KEY}.LOCK_TIME_SLOT`;
export const LOCK_TIME_SLOT_FAIL = `${KEY}.LOCK_TIME_SLOT_FAIL`;
export const LOCK_TIME_SLOT_SUCCESS = `${KEY}.LOCK_TIME_SLOT_SUCCESS`;

export const lockTimeSlot = (data: LockSlotRequestPayload) => ({
  type: LOCK_TIME_SLOT,
  payload: {
    client: 'scheduling',
    request: {
      method: 'post',
      url: '/slots/lock',
      data
    }
  }
});

/**
 * Unlock Appointment Slot
 */
export const UNLOCK_TIME_SLOT = `${KEY}.UNLOCK_TIME_SLOT`;
export const UNLOCK_TIME_SLOT_FAIL = `${KEY}.UNLOCK_TIME_SLOT_FAIL`;
export const UNLOCK_TIME_SLOT_SUCCESS = `${KEY}.UNLOCK_TIME_SLOT_SUCCESS`;

export function unlockTimeSlot(data: LockSlotRequestPayload) {
  return {
    type: UNLOCK_TIME_SLOT,
    payload: {
      client: 'scheduling',
      request: {
        method: 'patch',
        url: '/slots/unlock',
        data
      }
    }
  };
}

export const SET_REASON_FOR_VISIT = `${KEY}.SET_REASON_FOR_VISIT`;
export const setReasonForVisit = (reasonForVisit: string) => ({
  type: SET_REASON_FOR_VISIT,
  payload: reasonForVisit
});

// Communicates/Books appointment with Cerner
export const SUBMIT_NEW_APPOINTMENT = `${KEY}.SUBMIT_NEW_APPOINTMENT`;
export const SUBMIT_NEW_APPOINTMENT_FAIL = `${KEY}.SUBMIT_NEW_APPOINTMENT_FAIL`;
export const SUBMIT_NEW_APPOINTMENT_SUCCESS = `${KEY}.SUBMIT_NEW_APPOINTMENT_SUCCESS`;

export const submitNewAppointment = (data: SubmitAppointmentRequestPayload): ReduxAxiosAction => ({
  type: SUBMIT_NEW_APPOINTMENT,
  payload: {
    client: 'scheduling',
    request: {
      method: 'post',
      url: '/submitappointment',
      data
    }
  }
});

/**
 * Book an appointment
 */
export const CONFIRM_AND_BOOK_APPOINTMENT = `${KEY}.CONFIRM_AND_BOOK_APPOINTMENT`;
export const CONFIRM_AND_BOOK_APPOINTMENT_FAIL = `${KEY}.CONFIRM_AND_BOOK_APPOINTMENT_FAIL`;
export const CONFIRM_AND_BOOK_APPOINTMENT_SUCCESS = `${KEY}.CONFIRM_AND_BOOK_APPOINTMENT_SUCCESS`;

export const confirmAndBookAppointment = (payload: SubmitAppointmentRequestPayload) => async (
  dispatch: Dispatch<AnyAction>
) => {
  dispatch({ type: CONFIRM_AND_BOOK_APPOINTMENT });

  const confirmAndBookAppointmentRes = await dispatch(submitNewAppointment(payload));

  if (confirmAndBookAppointmentRes.error) {
    Logger.debug(`${LoggingModule.booking}`, confirmAndBookAppointmentRes);

    return dispatch({
      type: CONFIRM_AND_BOOK_APPOINTMENT_FAIL,
      error: confirmAndBookAppointmentRes.error
    });
  }

  return dispatch({
    type: CONFIRM_AND_BOOK_APPOINTMENT_SUCCESS,
    payload: confirmAndBookAppointmentRes
  });
};

/**
 * Modify Appointment
 * Sets the booking flow up for being able to modify/reschedule an appointment
 */
export const MODIFY_APPOINTMENT = `${KEY}.MODIFY_APPOINTMENT`;
export const MODIFY_APPOINTMENT_FAIL = `${KEY}.MODIFY_APPOINTMENT_FAIL`;
export const MODIFY_APPOINTMENT_SUCCESS = `${KEY}.MODIFY_APPOINTMENT_SUCCESS`;

export const modifyAppointment = (appointment: BookedAppointment) => async (
  dispatch: RootDispatch,
  getState: GetState
) => {
  dispatch({
    type: MODIFY_APPOINTMENT,
    payload: appointment.appointmentId
  });
  const state = getState();
  const { consumerId, providerPersonnelId, providerDisplayName } = appointment;

  // get patient to set
  const patientsStore = patientsDataSelector(state);

  let patientsResponse;
  let patientsData;
  if (!patientsStore.length) {
    patientsResponse = await dispatch(getPatients(false));
    if (patientsResponse?.error) {
      return dispatch({
        type: MODIFY_APPOINTMENT_FAIL,
        error: patientsResponse.error
      });
    }
    patientsData = [
      patientsResponse?.payload.data.consumer,
      ...patientsResponse?.payload.data.grantors
    ];
  }

  const patientList = patientsData || patientsStore;
  const patient = patientList.find((p: Patient) => p.consumerId === consumerId);

  if (!patient) {
    return dispatch({
      type: MODIFY_APPOINTMENT_FAIL,
      error: 'Could not find patient for appointment.'
    });
  }

  // get doctor to set (resourceId's aren't consistently strings vs numbers)
  const doctors = allProvidersSelector(state);
  let doctor = doctors.find(d => {
    return d.cernerPersonnelId === providerPersonnelId;
  });

  if (!doctor) {
    doctor = {
      cernerResourceId: providerPersonnelId,
      providerDisplayName,
      consumerIdList: [consumerId],
      displayName: providerDisplayName
    };
  }

  dispatch(getLocationTimeSlots(appointment));

  // dispatch success
  return dispatch({
    type: MODIFY_APPOINTMENT_SUCCESS,
    payload: {
      patient,
      doctor,
      groupedVisitType: {
        name: appointment.appointmentReasonDesc,
        visitTypes: [{ visitTypeId: appointment.visitTypeId }]
      },
      appointmentLocation: { ...appointment.appointmentLocation }
    }
  });
};

/**
 * Cancel Appointment
 */
export const CANCEL_APPOINTMENT = `${KEY}.CANCEL_APPOINTMENT`;
export const CANCEL_APPOINTMENT_FAIL = `${KEY}.CANCEL_APPOINTMENT_FAIL`;
export const CANCEL_APPOINTMENT_SUCCESS = `${KEY}.CANCEL_APPOINTMENT_SUCCESS`;

export const cancelAppointment = (data: CancelAppointmentRequestPayload): ReduxAxiosAction => ({
  type: CANCEL_APPOINTMENT,
  payload: {
    client: 'scheduling',
    request: {
      method: 'post',
      url: '/cancelappointment',
      data
    }
  }
});

export const RESCHEDULE_APPOINTMENT = `${KEY}.RESCHEDULE_APPOINTMENT`;
export const RESCHEDULE_APPOINTMENT_FAIL = `${KEY}.RESCHEDULE_APPOINTMENT_FAIL`;
export const RESCHEDULE_APPOINTMENT_SUCCESS = `${KEY}.RESCHEDULE_APPOINTMENT_SUCCESS`;

export const rescheduleAppointment = (
  data: RescheduleAppointmentRequestPayload
): ReduxAxiosAction => ({
  type: RESCHEDULE_APPOINTMENT,
  payload: {
    client: 'scheduling',
    request: {
      method: 'post',
      url: '/rescheduleappointment',
      data
    }
  }
});

export const RESET_PROVIDER = `${KEY}.RESET_PROVIDER`;

export const resetProvider = () => ({
  type: RESET_PROVIDER
});

export const CLEAR_APPOINTMENT = `${KEY}.CLEAR_APPOINTMENT`;

export const clearAppointment = () => ({
  type: CLEAR_APPOINTMENT
});

export const clearGroupedVisitTypes = () => ({
  type: CLEAR_GROUPED_VISIT_TYPE
});

export const clearAppointmentDetails = () => (dispatch: Function) => {
  dispatch(clearProviderForBooking());
  dispatch(clearAppointment());
  dispatch(resetProvider());
  dispatch(clearGroupedVisitTypes());
  dispatch(clearCorrelationIDAction());
};

export const START_BOOKING_FROM_COST_ESTIMATOR = `${KEY}.START_BOOKING_FROM_COST_ESTIMATOR`;
export const START_BOOKING_FROM_COST_ESTIMATOR_FAIL = `${KEY}.START_BOOKING_FROM_COST_ESTIMATOR_FAIL`;
export const START_BOOKING_FROM_COST_ESTIMATOR_SUCCESS = `${KEY}.START_BOOKING_FROM_COST_ESTIMATOR_SUCCESS`;

export const startBookingFromCostEstimator = (data): Thunk => async (
  dispatch: Function,
  getState: () => RootState
) => {
  dispatch({ type: START_BOOKING_FROM_COST_ESTIMATOR });
  const state = getState();
  clearAppointment();
  const patient = profileConsumerSelector(state);

  await dispatch(validateSelectedPatientInsurance(patient.consumerId));
  await dispatch(setDoctor(data.providerDetailsForBooking));

  return dispatch({ type: START_BOOKING_FROM_COST_ESTIMATOR_SUCCESS });
};

/**
 * shareAppointment action for transmit
 */
export const SHARE_APPOINTMENT = `${KEY}.SHARE_APPOINTMENT`;
export const SHARE_APPOINTMENT_FAIL = `${KEY}.SHARE_APPOINTMENT_FAIL`;
export const SHARE_APPOINTMENT_SUCCESS = `${KEY}.SHARE_APPOINTMENT_SUCCESS`;

export const shareAppointment = (
  appointmentId: string,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string
): Thunk => (dispatch: Function, getState: () => RootState) => {
  return dispatch({
    type: SHARE_APPOINTMENT,
    payload: {
      client: 'ccd',
      request: {
        method: 'post',
        url: '/sharedocs',
        headers: {
          'x-appointment-id': appointmentId,
          'x-dfd-consumer-id': currentAccountConsumerIdSelector(getState())
        },
        data: {
          toAddress,
          message,
          useSecureEmail,
          contactPhoneNumber
        }
      }
    }
  });
};
